/* Coria: sibling product choices preserve each product's original Shopify page. */
(() => {
  if (customElements.get('coria-product-choices')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const requests = new Map();
  const pageSize = 12;
  let sequence = 0;

  function getCatalog(url) {
    if (!requests.has(url)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const pending = fetch(url, { signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error('Selector unavailable');
          return response.json();
        })
        .catch(error => { requests.delete(url); throw error; })
        .finally(() => clearTimeout(timeout));
      requests.set(url, pending);
    }
    return requests.get(url);
  }

  function thumbnail(source) {
    try {
      const url = new URL(source, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      // A small CDN rendition of the same original image; store images are untouched.
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) url.searchParams.set('width', '96');
      return url.href;
    } catch (_) { return ''; }
  }

  function productImage(source) {
    try {
      const url = new URL(source, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) url.searchParams.set('width', '1200');
      return url.href;
    } catch (_) { return ''; }
  }


  /* Coria: eticheta scurta pentru casutele de nuanta (casutele au ~57px pe telefon) */
  const cfShortLabel = (item) => {
    const raw = String(item.label || item.title || '').trim();
    if (raw.length <= 10) return raw;
    const stop = /^(baza|base|rubber|coria|oja|semipermanenta|gel|uv|led|top|coat|polygel|builder|polish|professional|perfect|ml|g|gr|de|pentru|unghii|color|colour|nr\.?)$/i;
    const words = raw.replace(/\b\d+\s*(ml|g|gr)\b/ig, ' ').split(/[\s,–-]+/).filter(Boolean);
    let numIdx = -1;
    words.forEach((w, i) => { if (/^(nr\.?)?\d{1,4}$/i.test(w)) numIdx = i; });
    if (numIdx >= 0) {
      const num = words[numIdx].replace(/^nr\.?/i, '');
      const before = words.slice(0, numIdx).filter(w => !stop.test(w));
      const name = before.length ? before[before.length - 1] : '';
      const lbl = (name ? name + ' ' : '') + num;
      return lbl.length <= 14 ? lbl : num;
    }
    const rest = words.filter(w => !stop.test(w));
    const t = rest.slice(-2).join(' ');
    return t.length > 14 ? (rest[rest.length - 1] || raw).slice(0, 14) : (t || raw.slice(0, 14));
  };
  class CoriaProductChoices extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        const data = await getCatalog(this.dataset.manifest);
        this.catalog = data; // folosit de coria-choices-plus.js (stoc, pret, culoare)
        const manifestFamily = Object.keys(data.families || {}).find(name =>
          (data.families[name] || []).some(item => item.handle === this.dataset.handle)
        );
        const family = this.dataset.family || manifestFamily || '';
        if (family) {
          const families = { ...(data.families || {}) };
          const items = await this.familyItems(family, families);
          this.addFamily(family, items, families);
          this.hidden = !this.childElementCount;
          return;
        }
        const groups = (data.byHandle[this.dataset.handle] || [])
          .map(id => data.groups[id])
          .filter(group => group && group.items && group.items.length > 1)
          .sort((a, b) => (b.kind === 'volume') - (a.kind === 'volume'));
        for (const group of groups) this.addGroup(group);
        // Coria: orice alt produs (stickere, pile, accesorii...) primeste alternative din aceeasi categorie
        if (!this.childElementCount) { try { await this.addSimilarProducts(); } catch (_) { /* fara alternative */ } }
        this.hidden = !this.childElementCount;
      } catch (error) {
        this.replaceChildren();
        this.hidden = true;
        console.warn('Coria product choices:', error.message);
      }
    }

    async loadLiveFamily(family) {
      // Coria: produsele gamei se citesc live dupa eticheta "gama ..." (cautarea Shopify + sablonul search.cf-family),
      // asa ca orice produs nou cu eticheta potrivita apare singur in selector, cu volumul si nuanta/aroma lui.
      const familyTag = 'gama ' + normalize(family).trim();
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const results = [];
      try {
        for (let page = 1, pages = 1; page <= pages && page <= 8; page++) {
          const url = root + 'search?q=' + encodeURIComponent('tag:"' + familyTag + '"') + '&type=product&options%5Bprefix%5D=none&view=cf-family&page=' + page;
          const data = await getCatalog(url);
          pages = Number(data.pages) || 1;
          results.push(...(data.products || []).filter(item => (item.tags || []).some(tag => normalize(tag).trim() === familyTag)));
        }
      } catch (_) { /* rezerva mai jos: colectiile produsului */ }
      if (!results.length) {
        const expected = normalize(family).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const familyWords = expected.split('-').filter(Boolean);
        const candidates = String(this.dataset.collections || '').split(',').filter(Boolean).filter(handle => {
          const words = normalize(handle).split(/[^a-z0-9]+/).filter(Boolean);
          return familyWords.every(word => words.includes(word));
        });
        const lists = await Promise.all(candidates.map(handle => getCatalog(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=250').catch(() => ({}))));
        for (const item of lists.flatMap(data => data.products || [])) {
          if (!Array.isArray(item.tags) || !item.tags.some(tag => normalize(tag).trim() === familyTag)) continue;
          const variants = item.variants || [];
          const pick = variants.find(v => v.available) || variants[0] || {};
          results.push({ handle: item.handle, title: item.title || '', image: item.images?.[0]?.src || '', type: item.product_type || '',
            a: variants.some(v => v.available) ? 1 : 0, p: pick.price || '', d: String(item.published_at || item.created_at || '').slice(0, 10), v: pick.id });
        }
      }
      const volRe = /\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/i;
      const live = results.map(item => {
        const title = item.title || '';
        const volume = (title.match(volRe) || [''])[0].replace(/\s+/g, '');
        const bare = title.replace(new RegExp(volRe.source, 'ig'), '').replace(/\s+/g, ' ').trim();
        let label;
        if (/oja|gel|rubber|polygel|baza|pigment/i.test(item.type + ' ' + family)) {
          const m = bare.match(/(?:nr\.?\s*)?(\d{1,4}[a-z]?)\s*$/i);
          label = m ? m[1] : bare;
        } else if (/cleaner/i.test(family)) {
          label = normalize(bare).replace(/\b(cleaner|coria|cozy|seductive|copy)\b/g, '').replace(/\s+/g, ' ').trim();
        } else {
          label = normalize(bare);
        }
        return { handle: item.handle, title, image: item.image || '', volume, label, family, type: normalize(item.type || ''),
          a: item.a, p: item.p, d: item.d, v: item.v, r: item.r, rc: item.rc };
      });
      (this.liveItems || (this.liveItems = [])).push(...live);
      return live;
    }

    // static + live, fara dubluri (datele statice raman, cele live completeaza stocul, pretul si produsele noi)
    mergeFamily(staticItems, liveItems) {
      const byHandle = new Map();
      for (const item of staticItems || []) if (item?.handle) byHandle.set(item.handle, { ...item });
      for (const item of liveItems || []) {
        if (!item?.handle) continue;
        const known = byHandle.get(item.handle);
        if (known) Object.assign(known, { a: item.a, p: item.p, v: item.v, d: known.d || item.d });
        else byHandle.set(item.handle, item);
      }
      return [...byHandle.values()];
    }

    async familyItems(name, allFamilies) {
      this.familyCache = this.familyCache || {};
      if (!this.familyCache[name]) {
        let live = [];
        try { live = await this.loadLiveFamily(name); } catch (_) {}
        this.familyCache[name] = this.mergeFamily(allFamilies[name] || [], live);
        allFamilies[name] = this.familyCache[name];
      }
      return this.familyCache[name];
    }

    // Coria: lista live de produse dupa o interogare de cautare (tip de produs) sau dupa o colectie
    async loadQuery(query, maxPages) {
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const out = [];
      for (let page = 1, pages = 1; page <= pages && page <= (maxPages || 2); page++) {
        const data = await getCatalog(root + 'search?q=' + encodeURIComponent(query) + '&type=product&options%5Bprefix%5D=none&view=cf-family&page=' + page);
        pages = Number(data.pages) || 1;
        out.push(...(data.products || []));
      }
      return out;
    }

    async loadCollection(handle) {
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const data = await getCatalog(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=250');
      return (data.products || []).map(item => {
        const variants = item.variants || [];
        const pick = variants.find(v => v.available) || variants[0] || {};
        return { handle: item.handle, title: item.title || '', image: item.images?.[0]?.src || '', type: item.product_type || '',
          a: variants.some(v => v.available) ? 1 : 0, p: pick.price || '', d: String(item.published_at || '').slice(0, 10), v: pick.id };
      });
    }

    async addSimilarProducts() {
      if (this.dataset.similar === 'off') return;
      const type = (this.dataset.type || '').trim();
      const collection = (this.dataset.collection || '').trim();
      let list = [];
      let from = '';
      if (type) {
        list = (await this.loadQuery('product_type:"' + type.replace(/"/g, '') + '"', 2)).filter(item => normalize(item.type) === normalize(type));
        from = type;
      }
      if (list.length < 2 && collection) {
        list = await this.loadCollection(collection);
        from = this.dataset.collectionTitle || collection;
      }
      const unique = new Map();
      for (const item of list) if (item?.handle) unique.set(item.handle, item);
      if (!unique.has(this.dataset.handle)) {
        unique.set(this.dataset.handle, { handle: this.dataset.handle, title: this.dataset.currentTitle || '', image: this.dataset.currentImage || '', type });
      }
      const items = [...unique.values()];
      if (items.length < 2) return;
      // eticheta scurta: fara cuvintele comune de la inceput (ex. "Sticker Unghii") si fara volum
      const volRe = /\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/i;
      const words = items.map(item => String(item.title || '').replace(new RegExp(volRe.source, 'ig'), '').replace(/\s+/g, ' ').trim().split(' '));
      let common = 0;
      while (common < 4 && words.every(w => w.length > common + 1 && normalize(w[common]) === normalize(words[0][common]))) common++;
      const freq = new Map();
      for (const w of words) for (const word of new Set(w.map(normalize))) freq.set(word, (freq.get(word) || 0) + 1);
      const frequent = new Set([...freq].filter(([, n]) => words.length >= 4 && n / words.length >= .7).map(([word]) => word));
      const shaped = items.map((item, index) => ({
        ...item,
        family: from,
        volume: (String(item.title || '').match(volRe) || [''])[0].replace(/\s+/g, ''),
        label: (words[index].slice(common).filter(word => !frequent.has(normalize(word))).join(' ') || words[index].slice(common).join(' ') || item.title).replace(/^[\s\-\/|·,]+/, '').replace(/^(de|pentru|cu|si|la|din)\s+/i, ''),
        type: normalize(item.type || type)
      }));
      (this.liveItems || (this.liveItems = [])).push(...shaped);
      this.chooseTitle = 'Alege din ' + (from ? from.toLowerCase() : 'aceeasi categorie');
      this.classList.add('cfc-similar');
      this.addFamily('cf-similar ' + from, shaped, {});
    }

    addFamily(family, items, allFamilies) {
      if (/cleaner/i.test(family)) return this.addCleanerFamily(family, items);
      if (/^(rubber base|builder gel|polygel|oja semipermanenta)$/i.test(items.find(item => item.type)?.type || '')) {
        return this.addCollectionFamily(family, items, allFamilies);
      }
      const handle = this.dataset.selectedHandle || this.dataset.handle;
      const unique = new Map();
      for (const item of items) if (item?.handle) unique.set(item.handle, item);
      const cards = new Map();
      for (const item of unique.values()) {
        const key = normalize(item.label || item.title).replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/ig, '').trim();
        if (!cards.has(key)) cards.set(key, { label: item.label || item.title, image: item.image, options: [] });
        const card = cards.get(key);
        if (!card.image && item.image) card.image = item.image;
        card.options.push(item);
      }
      const current = [...cards.values()].find(card => card.options.some(item => item.handle === handle));
      if (!current || (cards.size < 2 && current.options.length < 2)) return;
      const block = document.createElement('section');
      block.className = 'coria-choice-group coria-family-group';
      const heading = document.createElement('div');
      heading.className = 'coria-choice-heading';
      const title = document.createElement('h3');
      const kind = /cleaner|cuticule|ulei/i.test(family) ? 'aromă' : 'nuanță / model';
      title.textContent = this.chooseTitle || ('Alege ' + kind);
      title.id = `coria-choice-heading-${++sequence}`;
      block.setAttribute('aria-labelledby', title.id);
      const selected = document.createElement('p');
      selected.className = 'coria-current-choice';
      const currentItem = current.options.find(item => item.handle === handle) || current.options[0] || {};
      selected.textContent = (currentItem.title || current.label || '') + (currentItem.volume ? ' · ' + currentItem.volume : '');
      heading.append(title, selected);
      block.append(heading);

      const list = document.createElement('div');
      list.className = 'coria-family-list';
      const allCards = [...cards.values()];
      for (const card of allCards) card.options.sort((a, b) => (parseFloat(a.volume) || 0) - (parseFloat(b.volume) || 0));
      let query = '';
      let limit = pageSize;
      const status = document.createElement('p');
      status.className = 'coria-choice-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'coria-choice-more';
      more.textContent = 'Arată mai multe';
      more.setAttribute('aria-label', 'Arată mai multe opțiuni: ' + title.textContent.toLowerCase());
      const draw = () => {
        const matched = allCards.filter(card => !query || normalize(card.label).includes(query));
        matched.sort((a, b) => (b.options.some(item => item.handle === handle)) - (a.options.some(item => item.handle === handle)));
        list.replaceChildren();
        for (const card of matched.slice(0, limit)) {
          const tile = document.createElement('div');
          tile.className = 'coria-family-card';
          if (card.options.some(item => item.handle === handle)) tile.classList.add('is-current');
          if (card.image) {
            const img = document.createElement('img');
            img.loading = 'lazy'; img.decoding = 'async'; img.width = 42; img.height = 42; img.alt = '';
            img.src = thumbnail(card.image);
            img.addEventListener('error', () => img.remove(), { once: true });
            tile.append(img);
          }
          const name = document.createElement('span');
          name.className = 'coria-family-name'; name.textContent = card.label;
          tile.append(name);
          const options = document.createElement('div');
          options.className = 'coria-family-sizes';
          for (const item of card.options) {
            const link = document.createElement('button');
            link.className = 'coria-family-size';
            link.type = 'button';
            link.dataset.handle = item.handle;
            link.textContent = item.volume || (item.handle === handle ? 'Ales' : 'Alege');
            link.title = item.title;
            link.setAttribute('aria-label', `${card.label}, ${item.volume || 'produs'}`);
            link.setAttribute('aria-pressed', item.handle === handle ? 'true' : 'false');
            if (item.handle === handle) link.classList.add('is-current');
            link.addEventListener('click', () => this.selectProduct(item, link, selected, status, block));
            options.append(link);
          }
          tile.append(options); list.append(tile);
        }
        status.textContent = matched.length ? `${Math.min(limit, matched.length)} din ${matched.length} opțiuni` : 'Nu există rezultate.';
        more.hidden = limit >= matched.length;
      };
      if (allCards.length > pageSize) {
        const input = document.createElement('input');
        input.type = 'search';
        input.placeholder = this.chooseTitle ? 'Cauta in ' + this.chooseTitle.replace(/^Alege din /, '') : (kind === 'aromă' ? 'Caută aroma' : 'Caută nuanța sau modelul');
        input.setAttribute('aria-label', 'Caută: ' + title.textContent.toLowerCase());
        input.className = 'coria-choice-search';
        input.autocomplete = 'off';
        input.addEventListener('input', () => { query = normalize(input.value); limit = pageSize; draw(); });
        block.append(input);
      }
      more.addEventListener('click', () => { limit += pageSize; draw(); });
      const footer = document.createElement('div');
      footer.className = 'coria-choice-footer';
      footer.append(status, more);
      block.append(list, footer);
      this.append(block);
      draw();
    }

    makeBlock(titleText, currentText) {
      const block = document.createElement('section');
      block.className = 'coria-choice-group coria-family-group';
      const heading = document.createElement('div');
      heading.className = 'coria-choice-heading';
      const title = document.createElement('h3');
      title.textContent = titleText;
      title.id = `coria-choice-heading-${++sequence}`;
      block.setAttribute('aria-labelledby', title.id);
      const selected = document.createElement('p');
      selected.className = 'coria-current-choice';
      selected.textContent = currentText || '';
      heading.append(title, selected);
      block.append(heading);
      const status = document.createElement('p');
      status.className = 'coria-choice-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const footer = document.createElement('div');
      footer.className = 'coria-choice-footer';
      footer.append(status);
      block.append(footer);
      this.append(block);
      return { block, title, selected, status };
    }

    addCleanerFamily(family, items) {
      const unique = new Map();
      for (const item of items) if (item?.handle) unique.set(item.handle, item);
      const products = [...unique.values()];
      const current = products.find(item => item.handle === (this.dataset.selectedHandle || this.dataset.handle));
      if (!current) return;
      const aromas = new Map();
      const volumes = new Set();
      for (const item of products) {
        const label = item.label || item.title.replace(/\bcleaner\b/ig, '').replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|l)\b/ig, '').trim();
        const key = normalize(label);
        const volume = normalize(item.volume);
        if (!key || !volume) continue;
        volumes.add(volume);
        if (!aromas.has(key)) aromas.set(key, { label, image: item.image || '', options: [] });
        const aroma = aromas.get(key);
        if (!aroma.image && item.image) aroma.image = item.image;
        aroma.options.push({ ...item, label, _volume: volume });
      }
      const sortedVolumes = [...volumes].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0));
      const groups = this.makeBlock('Alege cantitatea', current.title);
      groups.block.classList.add('coria-cleaner-selector');
      const volumeList = document.createElement('div');
      volumeList.className = 'coria-choice-list is-volume coria-cleaner-volumes';
      const aromaHeading = document.createElement('div');
      aromaHeading.className = 'coria-choice-heading coria-aroma-heading';
      const aromaTitle = document.createElement('h3');
      aromaTitle.textContent = 'Alege aroma';
      const selectedVolume = normalize(current.volume);
      const aromaList = document.createElement('div');
      aromaList.className = 'coria-family-list coria-cleaner-aromas';
      let volume = sortedVolumes.includes(selectedVolume) ? selectedVolume : sortedVolumes[0];
      let query = '';
      let limit = pageSize;
      const search = document.createElement('input');
      search.type = 'search';
      search.placeholder = 'Caută aroma';
      search.setAttribute('aria-label', 'Caută aroma');
      search.className = 'coria-choice-search';
      search.autocomplete = 'off';
      search.addEventListener('input', () => { query = normalize(search.value); limit = pageSize; drawAromas(); });
      const more = document.createElement('button');
      more.type = 'button'; more.className = 'coria-choice-more'; more.textContent = 'Arată mai multe';
      const drawAromas = () => {
        const matched = [...aromas.values()].map(aroma => ({ ...aroma, item: aroma.options.find(option => option._volume === volume) }))
          .filter(aroma => aroma.item && (!query || normalize(aroma.label).includes(query)));
        matched.sort((a, b) => (b.item.handle === (this.dataset.selectedHandle || this.dataset.handle)) - (a.item.handle === (this.dataset.selectedHandle || this.dataset.handle)) || a.label.localeCompare(b.label));
        aromaList.replaceChildren();
        for (const aroma of matched.slice(0, limit)) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'coria-family-size coria-aroma-option';
          button.dataset.handle = aroma.item.handle;
          button.setAttribute('aria-pressed', aroma.item.handle === (this.dataset.selectedHandle || this.dataset.handle) ? 'true' : 'false');
          if (aroma.item.handle === (this.dataset.selectedHandle || this.dataset.handle)) button.classList.add('is-current');
          if (aroma.image) {
            const image = document.createElement('img'); image.loading = 'lazy'; image.decoding = 'async'; image.width = 34; image.height = 34; image.alt = ''; image.src = thumbnail(aroma.image); button.append(image);
          }
          const name = document.createElement('span'); name.textContent = aroma.label; button.append(name);
          button.addEventListener('click', () => this.selectProduct(aroma.item, button, groups.selected, groups.status, groups.block));
          aromaList.append(button);
        }
        groups.status.textContent = matched.length ? `${Math.min(limit, matched.length)} din ${matched.length} arome disponibile la ${volume}` : `Nu există arome disponibile la ${volume}.`;
        more.hidden = limit >= matched.length;
      };
      const drawVolumes = () => {
        volumeList.replaceChildren();
        for (const value of sortedVolumes) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'coria-choice'; button.textContent = value;
          button.setAttribute('aria-pressed', value === volume ? 'true' : 'false');
          if (value === volume) button.classList.add('is-current');
          button.addEventListener('click', () => {
            if (value === volume) return;
            volume = value; limit = pageSize; drawVolumes(); drawAromas();
            const currentAroma = [...aromas.values()].find(aroma => aroma.options.some(option => option.handle === (this.dataset.selectedHandle || this.dataset.handle)));
            const replacement = currentAroma?.options.find(option => option._volume === volume) || [...aromas.values()].map(aroma => aroma.options.find(option => option._volume === volume)).find(Boolean);
            if (replacement) {
              const targetButton = [...aromaList.querySelectorAll('[data-handle]')].find(el => el.dataset.handle === replacement.handle);
              if (targetButton) this.selectProduct(replacement, targetButton, groups.selected, groups.status, groups.block);
            }
          });
          volumeList.append(button);
        }
      };
      more.addEventListener('click', () => { limit += pageSize; drawAromas(); });
      aromaHeading.append(aromaTitle);
      groups.block.append(volumeList, aromaHeading, search, aromaList, more);
      drawVolumes(); drawAromas();
    }

    addCollectionFamily(family, items, allFamilies) {
      const type = normalize(items.find(item => item.type)?.type || '');
      const collections = Object.entries(allFamilies).filter(([, products]) =>
        (products || []).some(item => normalize(item.type) === type)
      );
      if (collections.length < 2) return this.addFamilyFallback(family, items);
      const current = items.find(item => item.handle === (this.dataset.selectedHandle || this.dataset.handle));
      if (!current) return;
      const groups = this.makeBlock('Alege colecția', current.title);
      groups.block.classList.add('coria-gel-selector');
      const collectionList = document.createElement('div');
      collectionList.className = 'coria-choice-list coria-collection-list';
      const shadeHeading = document.createElement('div'); shadeHeading.className = 'coria-choice-heading coria-shade-heading';
      const shadeTitle = document.createElement('h3'); shadeTitle.textContent = 'Alege culoarea'; shadeHeading.append(shadeTitle);
      const shades = document.createElement('div'); shades.className = 'coria-choice-list coria-shade-list';
      const drawCollections = () => {
        collectionList.replaceChildren();
        for (const [name] of collections) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'coria-choice coria-collection-option';
          button.textContent = name.replace(/\b\w/g, char => char.toUpperCase());
          button.setAttribute('aria-pressed', name === family ? 'true' : 'false');
          if (name === family) button.classList.add('is-current');
          button.addEventListener('click', async () => {
            family = name;
            button.setAttribute('aria-busy', 'true');
            items = [...await this.familyItems(name, allFamilies)];
            button.removeAttribute('aria-busy');
            drawCollections(); drawShades();
            const target = items.find(item => item.label === current.label) || items[0];
            const targetButton = [...shades.querySelectorAll('[data-handle]')].find(el => el.dataset.handle === target?.handle);
            if (target && targetButton) this.selectProduct(target, targetButton, groups.selected, groups.status, groups.block);
          });
          collectionList.append(button);
        }
      };
      const drawShades = () => {
        shades.replaceChildren();
        const seen = new Set();
        const available = items.filter(item => {
          const key = normalize(item.label || item.title);
          if (!item.handle || seen.has(key)) return false;
          seen.add(key); return true;
        });
        /* eticheta: doar numarul nuantei; numele familiei apare doar cand doua nuante au acelasi numar */
        const cfLbl = new Map(), cfNum = new Map();
        available.forEach(it => { const l = cfShortLabel(it); const m = l.match(/(\d{1,4})$/); const n = m ? String(+m[1]) : null; cfLbl.set(it.handle, [l, m ? m[1] : null]); if (n) cfNum.set(n, (cfNum.get(n) || 0) + 1); });
        const cfPick = it => { const v = cfLbl.get(it.handle); if (!v || !v[1]) return v ? v[0] : cfShortLabel(it); return cfNum.get(String(+v[1])) > 1 ? v[0] : v[1]; };
        for (const item of available) {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'coria-choice coria-shade-option';
          button.dataset.handle = item.handle; button.title = item.title; button.setAttribute('aria-label', item.title);
          button.setAttribute('aria-pressed', item.handle === (this.dataset.selectedHandle || this.dataset.handle) ? 'true' : 'false');
          if (item.handle === (this.dataset.selectedHandle || this.dataset.handle)) button.classList.add('is-current');
          if (item.image) { const image = document.createElement('img'); image.loading = 'lazy'; image.decoding = 'async'; image.width = 42; image.height = 42; image.alt = ''; image.src = thumbnail(item.image); button.append(image); }
          const label = document.createElement('span'); label.textContent = cfPick(item); if (label.textContent.length >= 4) label.classList.add('cfc-long'); button.append(label);
          button.addEventListener('click', () => this.selectProduct(item, button, groups.selected, groups.status, groups.block));
          shades.append(button);
        }
      };
      groups.block.append(collectionList, shadeHeading, shades);
      drawCollections(); drawShades();
    }

    addFamilyFallback(family, items) {
      // A single collection still gets the standard shade chooser.
      const handle = this.dataset.selectedHandle || this.dataset.handle;
      const current = items.find(item => item.handle === handle);
      if (!current || items.length < 2) return;
      const groups = this.makeBlock('Alege culoarea', current.title);
      const list = document.createElement('div'); list.className = 'coria-choice-list coria-shade-list';
      for (const item of items) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'coria-choice coria-shade-option'; button.dataset.handle = item.handle; button.title = item.title; button.textContent = cfShortLabel(item);
        button.setAttribute('aria-pressed', item.handle === handle ? 'true' : 'false');
        if (item.handle === handle) button.classList.add('is-current');
        button.addEventListener('click', () => this.selectProduct(item, button, groups.selected, groups.status, groups.block));
        list.append(button);
      }
      groups.block.append(list);
    }

    addGroup(group) {
      const handle = this.dataset.handle;
      const current = group.items.find(item => item.handle === handle);
      if (!current) return;
      const block = document.createElement('section');
      block.className = 'coria-choice-group';
      const heading = document.createElement('div');
      heading.className = 'coria-choice-heading';
      const title = document.createElement('h3');
      const shade = /^(rubber|builder|oja|polygel)\b/.test(group.name);
      title.textContent = group.kind === 'volume' ? 'Cantitate' : group.kind === 'aroma' ? 'Aromă' : shade ? 'Nuanță' : 'Model';
      title.id = `coria-choice-heading-${++sequence}`;
      block.setAttribute('aria-labelledby', title.id);
      const selected = document.createElement('p');
      selected.className = 'coria-current-choice';
      selected.textContent = current.label;
      heading.append(title, selected);
      block.append(heading);

      const list = document.createElement('div');
      list.className = 'coria-choice-list' + (group.kind === 'volume' ? ' is-volume' : group.kind === 'aroma' ? ' is-aroma' : '');
      let query = '';
      let limit = pageSize;
      const status = document.createElement('p');
      status.className = 'coria-choice-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'coria-choice-more';
      more.textContent = 'Arată mai multe';
      more.setAttribute('aria-label', 'Arată mai multe opțiuni: ' + title.textContent.toLowerCase());

      const draw = () => {
        const matched = group.items.filter(item => !query || normalize(item.label + ' ' + item.title).includes(query));
        matched.sort((a, b) => (b.handle === handle) - (a.handle === handle));
        list.replaceChildren();
        for (const item of matched.slice(0, limit)) {
          const link = document.createElement('button');
          link.className = 'coria-choice';
          link.type = 'button';
          link.dataset.handle = item.handle;
          link.title = item.title;
          link.setAttribute('aria-label', item.title);
          if (item.handle === handle) {
            link.classList.add('is-current');
            link.setAttribute('aria-pressed', 'true');
          } else {
            link.setAttribute('aria-pressed', 'false');
          }
          if (group.kind !== 'volume') {
            const src = thumbnail(item.handle === handle && this.dataset.currentImage ? this.dataset.currentImage : item.image);
            if (src) {
              const img = document.createElement('img');
              img.loading = 'lazy';
              img.decoding = 'async';
              img.width = 42;
              img.height = 42;
              img.alt = '';
              img.src = src;
              img.addEventListener('error', () => img.remove(), { once: true });
              link.append(img);
            }
          }
          const label = document.createElement('span');
          label.textContent = item.label;
          link.append(label);
          link.addEventListener('click', () => this.selectProduct(item, link, selected, status, block));
          list.append(link);
        }
        status.textContent = matched.length ? `${Math.min(limit, matched.length)} din ${matched.length} opțiuni` : 'Nu există rezultate.';
        more.hidden = limit >= matched.length;
      };

      if (group.items.length > pageSize) {
        const input = document.createElement('input');
        input.type = 'search';
        input.placeholder = group.kind === 'aroma' ? 'Caută aroma' : 'Caută numărul sau numele';
        input.setAttribute('aria-label', 'Caută: ' + title.textContent.toLowerCase());
        input.className = 'coria-choice-search';
        input.autocomplete = 'off';
        input.addEventListener('input', () => { query = normalize(input.value); limit = pageSize; draw(); });
        block.append(input);
      }
      more.addEventListener('click', () => { limit += pageSize; draw(); });
      const footer = document.createElement('div');
      footer.className = 'coria-choice-footer';
      footer.append(status, more);
      block.append(list, footer);
      this.append(block);
      draw();
    }

    async selectProduct(item, button, selected, status, block) {
      if (!item?.handle || button.disabled) return;
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const section = this.closest('.section-main-product') || document.querySelector('.section-main-product');
      const form = section?.querySelector('product-form-context form[data-type="add-to-cart-form"]');
      const variantId = form?.querySelector('[name="id"]');
      if (!form || !variantId) {
        status.textContent = 'Nu am putut pregăti produsul pentru coș. Reîncarcă pagina și încearcă din nou.';
        return;
      }
      button.disabled = true;
      block.setAttribute('aria-busy', 'true');
      status.textContent = 'Se actualizează produsul…';
      try {
        const product = await getCatalog(root + 'products/' + encodeURIComponent(item.handle) + '.js');
        const variant = (product.variants || []).find(value => value.available) || (product.variants || [])[0];
        if (!variant) throw new Error('Nu există variante pentru produs.');
        variantId.value = variant.id;
        variantId.disabled = false;
        variantId.dispatchEvent(new Event('change', { bubbles: true }));
        const quantity = form.querySelector('[name="quantity"]');
        if (quantity && variant.quantity_rule) {
          quantity.min = variant.quantity_rule.min || 1;
          quantity.step = variant.quantity_rule.increment || 1;
          if (variant.quantity_rule.max != null) quantity.max = variant.quantity_rule.max;
          else quantity.removeAttribute('max');
          quantity.value = Math.max(Number(quantity.value) || 1, Number(quantity.min) || 1);
        }
        const canAdd = Boolean(variant.available);
        section.querySelectorAll('[data-add-to-cart], [data-sticky-buy-button]').forEach(control => {
          control.disabled = !canAdd;
          if (canAdd) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', 'true');
        });
        section.querySelectorAll('.product-title-heading, .sticky--product-card .product--title').forEach(node => { node.textContent = product.title; });
        // formatul temei contine HTML (<span class=money>...</span> Lei): il curatam, ca pretul sa apara ca in restul paginii
        const money = cents => {
          const raw = window.Currency?.formatMoney ? window.Currency.formatMoney(cents, window.settings?.moneyFormat) : (Number(cents) / 100).toFixed(2) + ' Lei';
          return String(raw).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        };
        section.querySelectorAll('.product--actual-price').forEach(node => { const n = money(variant.price).match(/[\d.,]+/); const inner = node.querySelector('.money'); if (inner && n) inner.textContent = ' ' + n[0]; else node.textContent = money(variant.price); });
        section.querySelectorAll('.product--cut-price').forEach(node => {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            node.textContent = money(variant.compare_at_price); node.hidden = false;
          } else node.hidden = true;
        });
        // galeria completa a produsului ales (toate pozele + miniaturi), din Section Rendering API; rezerva: doar prima poza
        const image = variant.featured_image?.src || product.featured_image || product.images?.[0];
        let galleryDone = false;
        try {
          const host = section.closest('[id^="shopify-section-"]');
          const sid = host ? host.id.replace('shopify-section-', '') : '';
          const oldGallery = section.querySelector('media-gallery');
          if (sid && oldGallery) {
            const html = await fetch(root + 'products/' + encodeURIComponent(item.handle) + '?section_id=' + encodeURIComponent(sid), { credentials: 'same-origin' }).then(r => r.ok ? r.text() : '');
            const doc = html ? new DOMParser().parseFromString(html, 'text/html') : null;
            const fresh = doc && doc.querySelector('media-gallery');
            if (fresh) {
              fresh.querySelectorAll('img[data-src], img[data-srcset]').forEach(im => { if (im.dataset.src && !im.getAttribute('src')) im.src = im.dataset.src; });
              oldGallery.replaceWith(document.importNode(fresh, true));
              galleryDone = true;
              // descrierea, recenziile si detaliile produsului ales (altfel ramaneau de la nuanta veche)
              ['.product--description', '#recenzii', '.cc-facts-grid'].forEach(sel => {
                const olds = section.closest('[id^="shopify-section-"]').querySelectorAll(sel), news = doc.querySelectorAll(sel);
                olds.forEach((o, k) => { if (news[k]) o.replaceWith(document.importNode(news[k], true)); });
              });
              try { if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') window.jdgm.initializeWidgets(); } catch (_) {}
              // culoarea ojei din sticluta robotelului urmeaza nuanta aleasa (#rrggbb)
              try {
                const hx = (doc.querySelector('[data-cf-hex]')?.getAttribute('data-cf-hex') || '').trim();
                let pm = document.querySelector('[data-coria-paint-color]');
                if (/^#[0-9a-f]{6}$/i.test(hx)) { if (!pm) { pm = document.createElement('meta'); document.head.append(pm); } pm.setAttribute('data-coria-paint-color', hx); }
              } catch (_) {}
              const crumbs = document.querySelectorAll('.breadcrumb li:last-child, [class*=breadcrumb] li:last-child span, [class*=breadcrumb] [aria-current]');
              crumbs.forEach(c => { if (c.children.length === 0) c.textContent = product.title; });
              try { document.title = product.title + ' – Coria'; } catch (_) {}
              const nu = root + 'products/' + item.handle + location.search;
              if (location.pathname.indexOf('/products/' + item.handle) === -1) history.replaceState(history.state, '', nu);
              window.dispatchEvent(new Event('resize'));
              document.dispatchEvent(new CustomEvent('coria:gallery-replaced', { detail: { handle: item.handle } }));
            }
          }
        } catch (_) { galleryDone = false; }
        if (!galleryDone && image) {
          section.querySelectorAll('.main--product-image-slider .swiper-slide img, media-gallery .main--product-item img').forEach((primaryImage, idx) => {
            if (idx > 0) return;
            primaryImage.src = productImage(image);
            primaryImage.removeAttribute('srcset');
            primaryImage.alt = product.title;
          });
        }
        block.querySelectorAll('[data-handle]').forEach(control => {
          const isSelected = control.dataset.handle === item.handle;
          control.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          control.classList.toggle('is-current', isSelected);
        });
        block.querySelectorAll('.coria-family-card').forEach(card => {
          card.classList.toggle('is-current', Boolean(card.querySelector(`[data-handle="${CSS.escape(item.handle)}"]`)));
        });
        const volume = item.volume ? ' · ' + item.volume : '';
        selected.textContent = product.title + volume;
        status.textContent = canAdd ? 'Selectat. Îl poți adăuga în coș.' : 'Produsul este momentan indisponibil.';
        this.dataset.selectedHandle = item.handle;
        if (!canAdd) button.classList.add('is-unavailable');
        else button.classList.remove('is-unavailable');
        button.disabled = !canAdd;
        button.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
      } catch (error) {
        status.textContent = 'Nu am putut încărca produsul selectat. Încearcă din nou.';
        console.warn('Coria product selection:', error.message);
      } finally {
        if (!button.classList.contains('is-unavailable')) button.disabled = false;
        block.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('coria-product-choices', CoriaProductChoices);
})();
